/**
 * Профиль артикула: чистая модель — период, запросы к слою MCP «Бизнес» (факт `productDay`, метрики `pd*`),
 * разбор ответов в ряды по дням и плитки периода. Без React: покрыто profileModel.test.ts.
 *
 * Метрики одного запроса — не больше 10 и из одной области прав: показатели рекламы закрыты правом на финансы
 * (`BusinessFinance:READ`), остальные — правом на продажи. Поэтому профиль делится на несколько запросов:
 * без права на рекламу падает только блок рекламы, остальное остаётся.
 */

import { addDays, formatDate, todayIso } from '../analytics/shared/format'
import { ExplorerApiError } from '../metrics-explorer/api'
import { formatMetricValue, parseDecimal } from '../metrics-explorer/format'
import { commonPartialReason, issueCodesOf } from '../analytics/business/partialReason'
import { QUALITY_LABELS } from '../metrics-explorer/labels'
import type { ExplorerQueryBody, ExplorerResult } from '../metrics-explorer/types'
import type { ArticleProfileContext, ProfileMarketplace } from './types'

/** Тело запроса профиля: контракт `/business-web/query` принимает `filters`, а клиентский тип Исследователя — пока нет. */
export type ProfileQueryBody = ExplorerQueryBody & { filters?: Record<string, Array<string | number>> }

export type ProfileGroup = 'sales' | 'funnel' | 'ads' | 'price' | 'stock' | 'attrs'

/** Предел периода факта `productDay` (maxPeriodDays). */
export const MAX_PERIOD_DAYS = 366

export type PeriodChoice = '30' | '90' | '180' | '365' | 'page'
export type MarketplaceChoice = 'all' | ProfileMarketplace

export const PERIOD_CHOICES: ReadonlyArray<{ value: Exclude<PeriodChoice, 'page'>; label: string }> = [
  { value: '30', label: '30 дней' },
  { value: '90', label: '90 дней' },
  { value: '180', label: '180 дней' },
  { value: '365', label: 'Год' },
]

/** Метрики каждого запроса (id каталога MCP, факт `productDay`). */
export const GROUP_METRICS: Record<Exclude<ProfileGroup, 'attrs'>, string[]> = {
  sales: ['pdOrdersQty', 'pdOrdersRub', 'pdBuyoutsQty', 'pdBuyoutsRub', 'pdCancelsQty', 'pdReturnsQty'],
  funnel: ['pdOpenCount', 'pdCartCount', 'pdOpenToCart', 'pdCartToOrder', 'pdOpenToOrder'],
  ads: ['pdAdsSpendRub', 'pdAdsViews', 'pdAdsClicks', 'pdAdsOrders'],
  price: ['pdSellerPrice', 'pdBuyerPrice', 'pdCardPrice', 'pdSppShare'],
  stock: ['pdInStockDays', 'pdOosDays', 'pdRatingAvg', 'pdReviewsNew'],
}

/** Разрезы описания артикула: кабинет, категория, тема, макет (по состоянию на сборку дня). */
export const ATTRIBUTE_DIMENSIONS = ['marketplace', 'sellerId', 'categoryId', 'themeNumber', 'designCode']

/** Единицы метрик: подпись значений плиток и графиков (в ответе у цен `RUB/units`, у остатка — дни). */
export const METRIC_UNITS: Record<string, string> = {
  pdOrdersQty: 'units',
  pdOrdersRub: 'RUB',
  pdBuyoutsQty: 'units',
  pdBuyoutsRub: 'RUB',
  pdCancelsQty: 'units',
  pdReturnsQty: 'units',
  pdOpenCount: 'units',
  pdCartCount: 'units',
  pdOpenToCart: 'ratio',
  pdCartToOrder: 'ratio',
  pdOpenToOrder: 'ratio',
  pdAdsSpendRub: 'RUB',
  pdAdsViews: 'units',
  pdAdsClicks: 'units',
  pdAdsOrders: 'units',
  pdSellerPrice: 'RUB',
  pdBuyerPrice: 'RUB',
  pdCardPrice: 'RUB',
  pdSppShare: 'ratio',
  pdInStockDays: 'days',
  pdOosDays: 'days',
  pdRatingAvg: '',
  pdReviewsNew: 'units',
}

export const addDay = (iso: string, days: number): string => addDays(iso, days)

/** Список дат `from..to` включительно; пустой, если границы перепутаны. */
export function enumerateDays(from: string, to: string): string[] {
  if (!from || !to || from > to) return []
  const days: string[] = []
  let current = from
  // Предохранитель от неверной даты: не больше предела факта.
  while (current <= to && days.length <= MAX_PERIOD_DAYS) {
    days.push(current)
    current = addDays(current, 1)
  }
  return days
}

export const hasPagePeriod = (context?: ArticleProfileContext): boolean => Boolean(context?.from && context?.to && context.from <= context.to)

/** Период профиля: «N дней» — по вчера (сегодня источники ещё не догрузили), «страницы» — как у вызвавшей страницы, не больше года. */
export function resolvePeriod(choice: PeriodChoice, context?: ArticleProfileContext, now: Date = new Date()): { from: string; to: string } {
  const yesterday = addDays(todayIso(now), -1)
  if (choice === 'page' && context && hasPagePeriod(context)) {
    // Текущий месяц или период «до сегодня» заканчиваются в будущем: ось дней обрезается по вчера.
    const to = (context.to as string) > yesterday ? yesterday : (context.to as string)
    const from = context.from as string
    if (from <= to) {
      const earliest = addDays(to, -(MAX_PERIOD_DAYS - 1))
      return { from: from < earliest ? earliest : from, to }
    }
  }
  const days = choice === 'page' ? 90 : Number(choice)
  return { from: addDays(yesterday, -(days - 1)), to: yesterday }
}

/** Период по умолчанию: страницы, если она его передала, иначе 90 дней. */
export const defaultPeriodChoice = (context?: ArticleProfileContext): PeriodChoice => (hasPagePeriod(context) ? 'page' : '90')

/** Фильтры площадки и кабинета: кабинет — парой с площадкой, площадка без кабинета — фильтром. */
export function scopeOf(marketplace: MarketplaceChoice, context?: ArticleProfileContext): { sellers?: ProfileQueryBody['sellers']; marketplaceFilter: ProfileMarketplace | null } {
  const mp: ProfileMarketplace | null = marketplace === 'all' ? null : marketplace
  if (mp && context?.marketplace === mp && Number.isFinite(context.sellerId)) {
    return { sellers: [{ marketplace: mp, sellerId: context.sellerId as number }], marketplaceFilter: null }
  }
  return { marketplaceFilter: mp }
}

export interface ProfileScope {
  article: string
  from: string
  to: string
  marketplace: MarketplaceChoice
  context?: ArticleProfileContext
}

/** Тело запроса группы метрик по дням; `attrs` — описание артикула (кабинет, категория, тема, макет) за период. */
export function buildProfileRequest(group: ProfileGroup, scope: ProfileScope): ProfileQueryBody {
  const { sellers, marketplaceFilter } = scopeOf(scope.marketplace, scope.context)
  const filters: Record<string, Array<string | number>> = { article: [scope.article] }
  if (marketplaceFilter) filters.marketplace = [marketplaceFilter]
  const base = { from: scope.from, to: scope.to, ...(sellers ? { sellers } : {}), filters }
  if (group === 'attrs') {
    return { ...base, metrics: ['pdOrdersQty'], dimensions: ATTRIBUTE_DIMENSIONS, sort: [{ field: 'pdOrdersQty', direction: 'desc' }], limit: 50, offset: 0 }
  }
  return { ...base, metrics: [...GROUP_METRICS[group]], dimensions: ['day'], limit: 500, offset: 0 }
}

/** Ряд метрики по дням оси; дня нет в ответе (панель хранит только активные товар-дни) или значение неизвестно — `null`. */
export function seriesOf(result: ExplorerResult | null, metricId: string, days: readonly string[]): Array<number | null> {
  const byDay = new Map<string, number | null>()
  for (const row of result?.rows ?? []) {
    const day = row.dimensions.day
    if (day) byDay.set(day, parseDecimal(row.values[metricId] ?? null))
  }
  return days.map((day) => byDay.get(day) ?? null)
}

export const hasValues = (values: Array<number | null>): boolean => values.some((value) => value !== null)

/** ДРР по дням: расход рекламы / сумма заказов артикула того же дня; день без заказов — `null`, не ноль. */
export function drrSeries(spend: Array<number | null>, ordersRub: Array<number | null>): Array<number | null> {
  return spend.map((value, index) => {
    const orders = ordersRub[index]
    return value !== null && orders !== null && orders !== undefined && orders > 0 ? value / orders : null
  })
}

/** Наличие по дням: 1 — товар доступен к заказу, 0 — нет остатка, `null` — неизвестно. */
export function availabilitySeries(inStockDays: Array<number | null>): Array<number | null> {
  return inStockDays.map((value) => (value === null ? null : value > 0 ? 1 : 0))
}

export type ErrorKind = 'forbidden' | 'capacity' | 'other'

export function errorKind(error: unknown): ErrorKind {
  if (error instanceof ExplorerApiError) {
    if (error.code === 'FORBIDDEN' || error.status === 403) return 'forbidden'
    if (error.code === 'BUSINESS_QUERY_CAPACITY') return 'capacity'
  }
  return 'other'
}

export interface GroupState {
  data: ExplorerResult | null
  error: unknown | null
  loading: boolean
}

export interface ProfileTile {
  id: string
  label: string
  /** Готовая строка: «1 234 ₽», «—». */
  value: string
  /** «частично», «нет данных», «нет доступа» или пусто. */
  note: string | null
  /** Коды проблем качества, из-за которых стоит «частично» (только у плиток с такой пометкой). */
  partialCodes?: string[]
}

interface TileSpec {
  id: string
  label: string
  group: Exclude<ProfileGroup, 'attrs'>
  metric: string
  unit: string
}

const TILE_SPECS: TileSpec[] = [
  { id: 'orders', label: 'Заказы', group: 'sales', metric: 'pdOrdersQty', unit: 'units' },
  { id: 'ordersRub', label: 'Сумма заказов', group: 'sales', metric: 'pdOrdersRub', unit: 'RUB' },
  { id: 'buyouts', label: 'Выкупы', group: 'sales', metric: 'pdBuyoutsQty', unit: 'units' },
  { id: 'opens', label: 'Открытия карточки', group: 'funnel', metric: 'pdOpenCount', unit: 'units' },
  { id: 'openToOrder', label: 'Открытие → заказ', group: 'funnel', metric: 'pdOpenToOrder', unit: 'ratio' },
  { id: 'adSpend', label: 'Расход рекламы', group: 'ads', metric: 'pdAdsSpendRub', unit: 'RUB' },
  { id: 'sellerPrice', label: 'Цена продавца', group: 'price', metric: 'pdSellerPrice', unit: 'RUB' },
  { id: 'oosDays', label: 'Дней без остатка', group: 'stock', metric: 'pdOosDays', unit: 'days' },
  { id: 'rating', label: 'Оценка WB', group: 'stock', metric: 'pdRatingAvg', unit: '' },
]

const qualityOf = (value: string | undefined): 'complete' | 'partial' | 'unavailable' => (value === 'partial' || value === 'unavailable' ? value : 'complete')

function tileFor(spec: Pick<TileSpec, 'id' | 'label' | 'unit'>, value: number | null, quality: 'complete' | 'partial' | 'unavailable', state: GroupState, codes: string[] = []): ProfileTile {
  if (state.error) {
    const kind = errorKind(state.error)
    return { id: spec.id, label: spec.label, value: '—', note: kind === 'forbidden' ? 'нет доступа' : 'не загрузилось' }
  }
  if (!state.data) return { id: spec.id, label: spec.label, value: '—', note: null }
  if (value === null) return { id: spec.id, label: spec.label, value: '—', note: QUALITY_LABELS.unavailable }
  return { id: spec.id, label: spec.label, value: formatMetricValue(value, spec.unit), note: quality === 'partial' ? QUALITY_LABELS.partial : null, ...(quality === 'partial' ? { partialCodes: codes } : {}) }
}

/** ДРР периода: расход рекламы / сумма заказов артикула; без права на рекламу или без заказов — «—» с причиной. */
function drrTile(ads: GroupState, sales: GroupState): ProfileTile {
  const base = { id: 'drr', label: 'ДРР' }
  const failed = ads.error ?? sales.error
  if (failed) return { ...base, value: '—', note: errorKind(failed) === 'forbidden' ? 'нет доступа' : 'не загрузилось' }
  if (!ads.data || !sales.data) return { ...base, value: '—', note: null }
  const spend = parseDecimal(ads.data.totals.pdAdsSpendRub ?? null)
  const orders = parseDecimal(sales.data.totals.pdOrdersRub ?? null)
  if (spend === null || orders === null || orders <= 0) return { ...base, value: '—', note: QUALITY_LABELS.unavailable }
  const partial = qualityOf(ads.data.metricQuality.pdAdsSpendRub) === 'partial' || qualityOf(sales.data.metricQuality.pdOrdersRub) === 'partial'
  return { ...base, value: formatMetricValue(spend / orders, 'ratio'), note: partial ? QUALITY_LABELS.partial : null, ...(partial ? { partialCodes: [...issueCodesOf(ads.data, ['pdAdsSpendRub']), ...issueCodesOf(sales.data, ['pdOrdersRub'])] } : {}) }
}

/** Плитки периода: итог группы (`totals` ответа), «—» с причиной вместо нуля; ДРР считается из расхода и суммы заказов. */
export function buildProfileTiles(groups: Record<Exclude<ProfileGroup, 'attrs'>, GroupState>): ProfileTile[] {
  const tiles: ProfileTile[] = []
  for (const spec of TILE_SPECS) {
    const state = groups[spec.group]
    const value = parseDecimal(state.data?.totals[spec.metric] ?? null)
    tiles.push(tileFor(spec, value, qualityOf(state.data?.metricQuality[spec.metric]), state, issueCodesOf(state.data, [spec.metric])))
    if (spec.id === 'adSpend') tiles.push(drrTile(state, groups.sales))
  }
  return tiles
}

/**
 * Пометка качества без шума: у всех плиток со значением «частично» и причина одна — причина пишется один раз над
 * плитками (`reason`), а у самих плиток пометка снимается. Иначе (причины разные, плиток со значением меньше двух,
 * есть плитки без пометки) — плитки возвращаются как есть. Плитки «—» в расчёте не участвуют.
 */
export function collapsePartialTiles(tiles: ProfileTile[]): { tiles: ProfileTile[]; reason: string | null } {
  const shown = tiles.filter((tile) => tile.value !== '—')
  if (shown.length < 2 || shown.some((tile) => tile.note !== QUALITY_LABELS.partial)) return { tiles, reason: null }
  const reason = commonPartialReason(shown.map((tile) => tile.partialCodes ?? []))
  if (!reason) return { tiles, reason: null }
  return { tiles: tiles.map((tile) => (tile.note === QUALITY_LABELS.partial ? { ...tile, note: null } : tile)), reason }
}

/** Одна фраза о том, откуда артикул: «WB · Кабинет» по описанию (самая крупная по заказам строка). */
export interface ProfileAttributes {
  marketplaces: ProfileMarketplace[]
  sellers: Array<{ marketplace: ProfileMarketplace; sellerId: number; label: string }>
  category: string | null
  themeNumber: string | null
  designCode: string | null
}

export function parseAttributes(result: ExplorerResult | null): ProfileAttributes {
  const empty: ProfileAttributes = { marketplaces: [], sellers: [], category: null, themeNumber: null, designCode: null }
  if (!result || result.rows.length === 0) return empty
  const marketplaces: ProfileMarketplace[] = []
  const sellers: ProfileAttributes['sellers'] = []
  for (const row of result.rows) {
    const mp = row.dimensions.marketplace === 'ozon' ? 'ozon' : row.dimensions.marketplace === 'wb' ? 'wb' : null
    if (!mp) continue
    if (!marketplaces.includes(mp)) marketplaces.push(mp)
    const sellerId = Number(row.dimensions.sellerId)
    if (Number.isFinite(sellerId) && !sellers.some((item) => item.marketplace === mp && item.sellerId === sellerId)) {
      const label = result.entityLabels.sellers.find((item) => item.marketplace === mp && item.sellerId === sellerId)?.label
      sellers.push({ marketplace: mp, sellerId, label: label || `Кабинет ${sellerId}` })
    }
  }
  // Строки отсортированы по заказам: атрибуты берутся из самой крупной.
  const main = result.rows[0]
  const categoryId = Number(main.dimensions.categoryId)
  const category = Number.isFinite(categoryId) && main.dimensions.categoryId !== null
    ? result.entityLabels.categories.find((item) => item.categoryId === categoryId)?.label ?? `Категория ${categoryId}`
    : null
  return {
    marketplaces,
    sellers,
    category,
    themeNumber: main.dimensions.themeNumber || null,
    designCode: main.dimensions.designCode || null,
  }
}

export const MARKETPLACE_NAMES: Record<ProfileMarketplace, string> = { wb: 'Wildberries', ozon: 'Ozon' }

/** Подпись периода для заголовка: «01.08.2026 — 30.08.2026». */
export const periodLabel = (from: string, to: string): string => `${formatDate(from)} — ${formatDate(to)}`

/** Ссылка «Открыть в Исследователе»: метрики профиля, разрез «артикул» и тот же период; фильтра по артикулу в URL Исследователя нет. */
export function explorerHref(scope: ProfileScope, metrics: readonly string[]): string {
  const params = new URLSearchParams()
  params.set('m', metrics.join(','))
  params.set('d', 'article')
  params.set('from', scope.from)
  params.set('to', scope.to)
  params.set('o', `${metrics[0]}:desc`)
  const { sellers } = scopeOf(scope.marketplace, scope.context)
  if (sellers?.length) params.set('s', sellers.map((item) => `${item.marketplace}:${item.sellerId}`).join(','))
  return `/analytics/explorer?${params.toString()}`
}
